import React from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { AnnouncerV2 } from '@atlassian/jira-accessibility/src/ui/announcer-v2/index.tsx';
import { Fill } from '@atlassian/jira-common-components-skeleton/src/fill.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { SearchField } from '@atlassian/jira-searchfield/src/index.tsx';
import { LOADING } from '../../../constants.tsx';
import type { SidebarFilterComponentProps } from '../../../types.tsx';
import messages from './messages.tsx';

export const Filter = ({
	onChange,
	asyncState,
	filter,
	totalAvailableItemsCount,
	disabledReason,
}: SidebarFilterComponentProps) => {
	const { formatMessage } = useIntl();
	const onSearchFieldChange = (searchTerm: string, analyticsEvent?: UIAnalyticsEvent) =>
		onChange({ name: searchTerm }, analyticsEvent);
	const totalCountMessage = fg('jira-issue-terminology-refresh-m3')
		? messages.totalCountIssueTermRefresh
		: messages.totalCount;
	const message = filter.name ? messages.matchingItemsCount : totalCountMessage;
	return (
		<SearchFieldWrapper>
			{expValEquals(
				'tmp_issue_type_edit_changes_reducing_duplicate_cfs',
				'cohort',
				'experiment',
			) ? (
				<Box xcss={searchFieldLabelStyles}>
					<label htmlFor="filter">{formatMessage(messages.filterLabel)}</label>
				</Box>
			) : (
				<SearchFieldLabelOld>{formatMessage(messages.filterLabel)}</SearchFieldLabelOld>
			)}
			{disabledReason === undefined ? (
				<>
					<SearchField
						key="filter"
						value={filter.name}
						onChange={onSearchFieldChange}
						isAutocompleteDisabled
						placeholder={formatMessage(messages.filterPlaceholder)}
						shouldFitContainerWidth
						// placeholder for SearchField is hidden by default, so we have to override it
						placeholderAlwaysVisible
					/>
					<TotalCountWrapper>
						{asyncState === LOADING ? (
							<Fill />
						) : (
							<TotalCount>{formatMessage(message, { count: totalAvailableItemsCount })}</TotalCount>
						)}
					</TotalCountWrapper>
					<AnnouncerV2
						message={formatMessage(message, { count: totalAvailableItemsCount })}
						shouldAnnounce={asyncState !== LOADING}
					/>
				</>
			) : (
				<Tooltip content={formatMessage(disabledReason)}>
					<SearchField
						key="filter"
						value={filter.name}
						onChange={onSearchFieldChange}
						isAutocompleteDisabled
						placeholder={formatMessage(messages.filterPlaceholder)}
						shouldFitContainerWidth
						// placeholder for SearchField is hidden by default, so we have to override it
						placeholderAlwaysVisible
						isDisabled
					/>
				</Tooltip>
			)}
		</SearchFieldWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SearchFieldWrapper = styled.div({
	marginBottom: token('space.150'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text'),
});

const searchFieldLabelStyles = xcss({
	font: 'font.heading.xxsmall',
	color: 'color.text.subtle',
	paddingBottom: 'space.025',
	paddingTop: 'space.025',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SearchFieldLabelOld = styled.label({
	font: token('font.body.UNSAFE_small'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TotalCount = styled.p({
	font: token('font.body.UNSAFE_small'),
	textAlign: 'left',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TotalCountWrapper = styled.div({
	marginTop: token('space.050'),
	height: '16px',
});
